import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type {
  FilterState as ControllerFilterState,
  FilterControllerProps,
} from '@atlassian/filter-controller';
import FilterController from '@atlassian/filter-controller';

import { useAppUpdatesContext } from '@app/app/src/AppContext';
import { useAnalyticsEvent, appUpdatesUIEvents } from '@app/analytics';
import { fg, featureGateKeys } from '@app/feature-gates';

import { useUrlFilterSync } from '../hooks';
import { toSingleOptions } from '../utils/filterControllerUtils';
import {
  useChangeTypeFilterConfig,
  useChangeTypesFromOptions,
} from '../filter-config/ChangeTypeFilterConfig';
import {
  getImpactRatingsFromOptions,
  useImpactRatingFilterConfig,
} from '../filter-config/ImpactRatingFilterConfig';
import {
  getProductsFromOptions,
  useProductFilterConfig,
} from '../filter-config/ProductFilterConfig';
import {
  dateRangeFilterFieldId,
  getEffectiveFrom,
} from '../filter-config/RecentlyPublishedFilterConfig';
import {
  getStatusesFromOptions,
  useStatusFilterConfig,
} from '../filter-config/StatusFilterConfig';
import {
  getTagsFromOptions,
  useTagFilterConfig,
} from '../filter-config/TagFilterConfig';

const messages = defineMessages({
  searchLabel: {
    id: 'appUpdates.search.label',
    defaultMessage: 'Search release notes',
    description: 'Accessibility label for search input',
  },
  searchPlaceholder: {
    id: 'appUpdates.search.placeholder',
    defaultMessage: 'Search release notes',
    description: 'Placeholder text for search input',
  },
  recentlyPublished: {
    id: 'appUpdates.filters.recentlyPublished.label-nonfinal',
    description: 'Label for the new this week filter chip',
    defaultMessage: 'New this week',
  },
});

const toBooleanFilter = (
  option: ControllerFilterState['filters'][string],
): boolean => option === true;

export const AppUpdatesFilters: React.FC = () => {
  const { formatMessage } = useIntl();
  const { state, dispatch } = useAppUpdatesContext();
  const { syncFiltersToUrl } = useUrlFilterSync();
  const { sendUIEvent } = useAnalyticsEvent();
  const [filterControllerKey, setFilterControllerKey] = React.useState(0);
  const shouldIgnoreNextApplyRef = React.useRef(false);

  const productFilterConfig = useProductFilterConfig();
  const statusFilterConfig = useStatusFilterConfig();
  const changeTypeFilterConfig = useChangeTypeFilterConfig();
  const impactRatingFilterConfig = useImpactRatingFilterConfig();
  const tagFilterConfig = useTagFilterConfig();
  const getChangeTypesFromOptions = useChangeTypesFromOptions();
  const { draftFilters } = state.updates;
  const shouldShowImpactFilter = fg(featureGateKeys.aiImpactAndContextTags);
  const shouldShowTagFilter =
    shouldShowImpactFilter && fg(featureGateKeys.contextualTagsBeta);

  const filters = React.useMemo<FilterControllerProps['filters']>(() => {
    const nextFilters: FilterControllerProps['filters'] = [
      {
        type: 'toggle',
        fieldId: dateRangeFilterFieldId,
        fieldLabel: formatMessage(messages.recentlyPublished),
        value: Boolean(draftFilters.effectiveFrom),
        defaultValue: false,
        onChange: (isSelected) => {
          dispatch({
            type: 'FILTER_EFFECTIVE_FROM_TOGGLE',
            payload: {
              effectiveFrom: isSelected ? getEffectiveFrom() : null,
            },
          });
        },
        testId: dateRangeFilterFieldId,
      },
      productFilterConfig,
      statusFilterConfig,
      changeTypeFilterConfig,
    ];

    if (shouldShowImpactFilter) {
      nextFilters.push(impactRatingFilterConfig);
    }

    if (shouldShowTagFilter) {
      nextFilters.push(tagFilterConfig);
    }

    return nextFilters;
  }, [
    changeTypeFilterConfig,
    dispatch,
    draftFilters.effectiveFrom,
    formatMessage,
    impactRatingFilterConfig,
    productFilterConfig,
    shouldShowImpactFilter,
    shouldShowTagFilter,
    statusFilterConfig,
    tagFilterConfig,
  ]);

  const handleApply = React.useCallback(
    (values: ControllerFilterState) => {
      const effectiveFrom = toBooleanFilter(
        values.filters[dateRangeFilterFieldId],
      )
        ? draftFilters.effectiveFrom
        : null;
      const selectedProducts = getProductsFromOptions(
        toSingleOptions(values.filters[productFilterConfig.fieldId]),
      );
      const selectedStatuses = getStatusesFromOptions(
        toSingleOptions(values.filters[statusFilterConfig.fieldId]),
      );
      const selectedChangeTypes = getChangeTypesFromOptions(
        toSingleOptions(values.filters[changeTypeFilterConfig.fieldId]),
      );
      const selectedImpactRatings = shouldShowImpactFilter
        ? getImpactRatingsFromOptions(
            toSingleOptions(values.filters[impactRatingFilterConfig.fieldId]),
          )
        : draftFilters.selectedImpactRatings;
      const selectedTags = shouldShowTagFilter
        ? getTagsFromOptions(
            toSingleOptions(values.filters[tagFilterConfig.fieldId]),
          )
        : null;
      const searchQuery = values.searchText || null;

      dispatch({
        type: 'FILTER_EFFECTIVE_FROM_TOGGLE',
        payload: { effectiveFrom },
      });
      dispatch({
        type: 'FILTER_APP_SELECT',
        payload: { selectedProducts },
      });
      dispatch({
        type: 'FILTER_STATUS_SELECT',
        payload: { selectedStatuses },
      });
      dispatch({
        type: 'FILTER_CHANGE_TYPE_SELECT',
        payload: { selectedChangeTypes },
      });
      if (shouldShowImpactFilter) {
        dispatch({
          type: 'FILTER_IMPACT_RATING_SELECT',
          payload: { selectedImpactRatings },
        });
      }
      if (shouldShowTagFilter || draftFilters.selectedTags) {
        dispatch({
          type: 'FILTER_TAG_SELECT',
          payload: { selectedTags },
        });
      }
      dispatch({
        type: 'FILTER_SEARCH_SELECT',
        payload: { searchQuery },
      });

      syncFiltersToUrl({
        selectedProducts,
        selectedStatuses,
        selectedChangeTypes,
        selectedImpactRatings,
        selectedTags,
        searchQuery,
        effectiveFrom,
        cursor: undefined,
      });

      dispatch({
        type: 'UPDATES_FILTERS_APPLY',
      });

      sendUIEvent(
        appUpdatesUIEvents.ui.appUpdatesApplyClicked(
          { source: 'appUpdatesListScreen' },
          selectedProducts?.map((p) => p.id).join(','),
          selectedStatuses?.map((s) => s.id).join(','),
          selectedChangeTypes?.map((c) => c.id).join(','),
          searchQuery ?? undefined,
        ).data,
      );
    },
    [
      changeTypeFilterConfig.fieldId,
      dispatch,
      draftFilters.effectiveFrom,
      draftFilters.selectedImpactRatings,
      draftFilters.selectedTags,
      getChangeTypesFromOptions,
      impactRatingFilterConfig.fieldId,
      productFilterConfig.fieldId,
      sendUIEvent,
      shouldShowImpactFilter,
      shouldShowTagFilter,
      statusFilterConfig.fieldId,
      syncFiltersToUrl,
      tagFilterConfig.fieldId,
    ],
  );

  const handleReset = React.useCallback(() => {
    shouldIgnoreNextApplyRef.current = true;

    syncFiltersToUrl({
      selectedProducts: null,
      selectedStatuses: null,
      selectedChangeTypes: null,
      selectedImpactRatings: null,
      selectedTags: null,
      searchQuery: null,
      effectiveFrom: null,
      cursor: undefined,
    });

    dispatch({ type: 'UPDATES_FILTERS_RESET' });
    setFilterControllerKey((currentKey) => currentKey + 1);
  }, [dispatch, syncFiltersToUrl]);

  const handleFilterControllerApply = React.useCallback(
    (values: ControllerFilterState) => {
      if (shouldIgnoreNextApplyRef.current) {
        shouldIgnoreNextApplyRef.current = false;
        return;
      }

      handleApply(values);
    },
    [handleApply],
  );

  return (
    <FilterController
      key={filterControllerKey}
      testId="app-updates-table-filters"
      filters={filters}
      isManual
      hideItemsCountFooter
      itemsCount={state.updates.data.length}
      onApply={handleFilterControllerApply}
      onReset={handleReset}
      searchInput={draftFilters.searchQuery ?? ''}
      searchInputAriaLabel={formatMessage(messages.searchLabel)}
      searchPlaceholder={formatMessage(messages.searchPlaceholder)}
    />
  );
};

export default AppUpdatesFilters;
